import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Site-wide SEO metadata (2026-10-01). Runs after each route change:
//  - canonical <link> + og:url always point at the current public path
//    (no query string), so Google never sees /templates etc. as a duplicate
//    of the home page;
//  - pages that don't manage their own meta get a real title/description
//    here. Pages using useDocumentMeta (the product pages, /demo) are left
//    alone -- their hook already set title/description for this route.
const SITE = "https://resumepilot360.com";
const DEFAULT_TITLE = "ResumePilot360 — AI Resume Builder, ATS Checker & Job Match";
const DEFAULT_DESC =
  "Build a job-ready resume with AI. 26 ATS-friendly templates, an ATS resume checker, job match, tailored resumes and cover letters. Start free.";

const SELF_MANAGED = new Set(["/ats-resume-checker", "/job-match", "/resume-optimizer", "/smart-style-studio", "/linkedin-to-resume", "/demo"]);
// Public resumes / portfolios / profiles set their own title (the person's
// name) -- never overwrite it.
const SELF_MANAGED_PREFIXES = ["/r/", "/p/", "/u/"];

export const PAGE_META = {
  "/templates": {
    title: "26 Free ATS-Friendly Resume Templates | ResumePilot360",
    description: "Browse 26 professional, ATS-friendly resume templates — classic, executive, modern and creative. Preview with real content and switch styles in one click.",
  },
  "/compare": {
    title: "ResumePilot360 vs Other Resume Builders | Compare Features",
    description: "Compare ResumePilot360 with other resume builders: AI resume writing, ATS checking, job match, tailored resumes, cover letters and pricing.",
  },
  "/about": {
    title: "About ResumePilot360 | AI Career Tools by SprytStack LLC",
    description: "ResumePilot360 helps job seekers build, tailor and check resumes with AI grounded in their real experience. Built by SprytStack LLC.",
  },
  "/contact": {
    title: "Contact ResumePilot360 | Support",
    description: "Questions about your resume, plan or billing? Contact the ResumePilot360 support team at support@resumepilot360.com.",
  },
  "/privacy": { title: "Privacy Policy | ResumePilot360", description: "How ResumePilot360 collects, uses and protects your personal data and resume content." },
  "/terms": { title: "Terms of Service | ResumePilot360", description: "The terms that govern your use of ResumePilot360." },
  "/refund": { title: "Refund Policy | ResumePilot360", description: "ResumePilot360 subscription cancellation and refund policy." },
  "/cookies": { title: "Cookie Policy | ResumePilot360", description: "How ResumePilot360 uses cookies and similar technologies." },
  "/login": { title: "Log in | ResumePilot360", description: DEFAULT_DESC },
  "/signup": { title: "Create your free account | ResumePilot360", description: "Create a free ResumePilot360 account — no credit card needed. Build your resume with AI and check it against any job." },
};

function setMeta(attr, key, value) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

export function SeoMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
    const url = `${SITE}${path === "/" ? "/" : path}`;

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
    setMeta("property", "og:url", url);

    if (SELF_MANAGED.has(path) || SELF_MANAGED_PREFIXES.some((x) => path.startsWith(x))) return;
    const meta = PAGE_META[path] || { title: DEFAULT_TITLE, description: DEFAULT_DESC };
    document.title = meta.title;
    setMeta("name", "description", meta.description);
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.description);
  }, [pathname]);
  return null;
}
